// Floating objects over the grid: charts and images. Each is anchored to a cell (plus an offset) and stored in the
// sheet's props; they can be selected, moved, resized, edited and deleted. Also: image upload and people chips.
import { indexToCol } from '../../engine/src/index.ts';
import type { Op } from '../../shared/ops.ts';
import { ApiError, api } from './api.ts';
import { CHART_TYPES, PALETTE, chartColumns, chartData, guessLayout, renderChart } from './charts.ts';
import type { ChartSpec, ChartType } from './charts.ts';
import * as cmd from './commands.ts';
import { dataRegion, parseRange, rangeText } from './panels.ts';
import type { PanelCtx } from './panels.ts';
import { ruleId } from './rules.ts';
import { themeOf } from './theme.ts';
import { drawingEditor } from './drawing.ts';
import type { Shape } from './drawing.ts';
import { slicerPanel, slicerPopup, slicerTitle } from './views.ts';
import type { Slicer } from './views.ts';
import { dialog, h, popup, promptText, toast } from './ui.ts';

export interface ImageSpec { id: string; url: string; anchor: ChartSpec['anchor']; w: number; h: number; alt?: string; drawing?: Shape[] }
type Kind = 'charts' | 'images' | 'slicers';
type Obj = (ChartSpec | ImageSpec | Slicer) & { kind: Kind };

export class ObjectLayer {
  private primary: string | null = null;
  private selectedIds = new Set<string>();
  private copiedImages: ImageSpec[] = [];
  private x: PanelCtx;
  private svgCache = new Map<string, { key: string; svg: string }>();
  private drag: { id: string; kind: Kind; mode: string; sx: number; sy: number; box: { x: number; y: number; w: number; h: number }; el: HTMLElement; group: { id: string; box: { x: number; y: number; w: number; h: number }; el: HTMLElement }[]; moved: boolean } | null = null;
  private lastSig = '';

  constructor(x: PanelCtx) {
    this.x = x;
    const layer = x.grid.objects;
    layer.addEventListener('mousedown', e => this.down(e));
    layer.addEventListener('dblclick', e => {
      const el = (e.target as HTMLElement).closest('.obj') as HTMLElement | null;
      if (el?.dataset.kind === 'charts') chartPanel(this.x, el.dataset.id!, this);
      if (el?.dataset.kind === 'slicers' && this.x.canEdit()) slicerPanel(this.x, el.dataset.id!);
      if (el?.dataset.kind === 'images' && this.x.canEdit()) { const spec = this.list().find(o => o.id === el.dataset.id) as ImageSpec | undefined; if (spec?.drawing) insertDrawing(this.x, this, spec); }
    });
    document.addEventListener('mousemove', e => this.move(e));
    document.addEventListener('mouseup', () => this.up());
    // clicking a cell deselects
    x.grid.el.addEventListener('mousedown', e => { if (this.selected && !(e.target as HTMLElement).closest('.obj')) { this.selected = null; this.render(true); } }, true);
    document.addEventListener('copy', e => this.copy(e), true);
    document.addEventListener('paste', e => this.paste(e), true);
  }
  get selected(): string | null { return this.primary; }
  set selected(id: string | null) {
    this.primary = id; this.selectedIds.clear(); if (id) this.selectedIds.add(id);
  }
  private list(): Obj[] {
    const p = this.x.model.sheet(this.x.sheet())?.props ?? {};
    return [...((p.charts ?? []) as ChartSpec[]).map(c => ({ ...c, kind: 'charts' as const })), ...((p.images ?? []) as ImageSpec[]).map(i => ({ ...i, kind: 'images' as const })),
      ...((p.slicers ?? []) as Slicer[]).map(s => ({ ...s, kind: 'slicers' as const }))];
  }
  private box(o: Obj): { x: number; y: number; w: number; h: number } {
    const a = o.anchor, p = this.x.grid.cellOffset(a.r, a.c);
    return { x: p.x + a.dx, y: p.y + a.dy, w: o.w, h: o.h };
  }
  /** Called on every grid draw; rebuilds only when objects, data, layout or selection changed. */
  render(force = false): void {
    if (this.drag) return;
    const objs = this.list();
    const sig = JSON.stringify([this.x.sheet(), this.x.model.version, this.primary, [...this.selectedIds].sort(), objs.map(o => [o, this.box(o)])]);
    if (!force && sig === this.lastSig) return;
    this.lastSig = sig;
    const layer = this.x.grid.objects;
    layer.replaceChildren(...objs.map(o => {
      const b = this.box(o), sel = this.selectedIds.has(o.id), active = o.id === this.primary;
      const el = h('div', { class: `obj ${o.kind === 'charts' ? 'chart' : o.kind === 'slicers' ? 'slicer' : 'image'}${sel ? ' sel' : ''}`, 'data-id': o.id, 'data-kind': o.kind, style: { left: `${b.x}px`, top: `${b.y}px`, width: `${b.w}px`, height: `${b.h}px` } });
      if (o.kind === 'charts') el.innerHTML = this.svg(o as ChartSpec);
      else if (o.kind === 'slicers') {
        const sl = o as Slicer, n = sl.hide?.length ?? 0;
        el.append(h('div', { class: 'slicer-title' }, slicerTitle(this.x, sl)), h('div', { class: `slicer-btn${n ? ' on' : ''}`, 'data-slicer': '1' }, h('span', {}, n ? `${n}개 항목 숨김` : '모두'), h('span', {}, '▾')));
      } else el.append(h('img', { src: (o as ImageSpec).url, alt: (o as ImageSpec).alt ?? '', draggable: false }));
      if (active) {
        for (const hd of ['nw', 'n', 'ne', 'e', 'se', 's', 'sw', 'w']) el.append(h('span', { class: `hdl ${hd}`, 'data-h': hd }));
        el.append(h('button', { class: 'obj-menu', title: '더보기', 'data-menu': '1' }, '⋮'));
      }
      return el;
    }));
  }
  private svg(spec: ChartSpec): string {
    const key = JSON.stringify([spec, this.x.model.version]), hit = this.svgCache.get(spec.id);
    if (hit?.key === key) return hit.svg;
    const svg = renderChart(spec.colors?.length ? spec : { ...spec, colors: themeOf(this.x).accents }, chartData(this.x.model, this.x.sheet(), spec));
    this.svgCache.set(spec.id, { key, svg });
    return svg;
  }

  // ----- mouse: select, move, resize -----
  private down(e: MouseEvent): void {
    const t = e.target as HTMLElement, el = t.closest('.obj') as HTMLElement | null;
    if (!el || e.button !== 0) return;
    e.preventDefault(); e.stopPropagation();
    this.x.grid.el.focus({ preventScroll: true });
    const id = el.dataset.id!, kind = el.dataset.kind as Kind;
    if (t.dataset.menu) { this.menu(id, kind, e.clientX, e.clientY); return; }
    if (t.closest('[data-slicer]')) { const b = t.closest('[data-slicer]')!.getBoundingClientRect(); slicerPopup(this.x, id, b.left, b.bottom); return; }
    if (kind === 'images' && e.shiftKey) {
      if (this.selectedIds.has(id) && this.selectedIds.size > 1) this.selectedIds.delete(id);
      else this.selectedIds.add(id);
      this.primary = this.selectedIds.has(id) ? id : [...this.selectedIds][0] ?? null;
      this.render(true); return;
    }
    if (!this.selectedIds.has(id) || kind !== 'images') this.selected = id;
    else this.primary = id;
    this.render(true);
    const o = this.list().find(q => q.id === id); if (!o || !this.x.canEdit()) return;
    const cur = this.x.grid.objects.querySelector(`.obj[data-id="${id}"]`) as HTMLElement;
    const mode = t.dataset.h ?? 'move';
    const group = kind === 'images' && mode === 'move' ? this.list().filter(q => q.kind === 'images' && q.id !== id && this.selectedIds.has(q.id)).map(q => ({ id: q.id, box: this.box(q), el: this.x.grid.objects.querySelector(`.obj[data-id="${q.id}"]`) as HTMLElement })) : [];
    this.drag = { id, kind, mode, sx: e.clientX, sy: e.clientY, box: this.box(o), el: cur, group, moved: false };
  }
  private move(e: MouseEvent): void {
    const d = this.drag; if (!d) return;
    const z = this.x.grid.zoom, dx = (e.clientX - d.sx) / z, dy = (e.clientY - d.sy) / z;
    if (Math.abs(dx) + Math.abs(dy) > 2) d.moved = true;
    let { x, y, w, h: hh } = d.box;
    if (d.mode === 'move') { x += dx; y += dy; }
    else {
      if (d.mode.includes('e')) w += dx;
      if (d.mode.includes('s')) hh += dy;
      if (d.mode.includes('w')) { x += dx; w -= dx; }
      if (d.mode.includes('n')) { y += dy; hh -= dy; }
      w = Math.max(40, w); hh = Math.max(30, hh);
    }
    Object.assign(d.el.style, { left: `${Math.max(0, x)}px`, top: `${Math.max(0, y)}px`, width: `${w}px`, height: `${hh}px` });
    if (d.mode === 'move') for (const q of d.group) Object.assign(q.el.style, { left: `${Math.max(0, q.box.x + dx)}px`, top: `${Math.max(0, q.box.y + dy)}px` });
    const svg = d.el.querySelector('svg'); if (svg) { svg.setAttribute('width', String(w)); svg.setAttribute('height', String(hh)); svg.setAttribute('preserveAspectRatio', 'none'); }
  }
  private up(): void {
    const d = this.drag; if (!d) return;
    this.drag = null;
    if (!d.moved) return;
    const s = d.el.style, x = parseFloat(s.left), y = parseFloat(s.top), w = Math.round(parseFloat(s.width)), hh = Math.round(parseFloat(s.height));
    const moved = [{ id: d.id, x, y, w, h: hh }, ...d.group.map(q => ({ id: q.id, x: parseFloat(q.el.style.left), y: parseFloat(q.el.style.top), w: q.box.w, h: q.box.h }))];
    if (moved.length === 1) {
      const cell = this.x.grid.cellAtCanvas(x, y), off = this.x.grid.cellOffset(cell.r, cell.c);
      this.update(d.kind, d.id, { anchor: { r: cell.r, c: cell.c, dx: Math.round(x - off.x), dy: Math.round(y - off.y) }, w, h: hh });
      return;
    }
    const positions = new Map(moved.map(q => { const cell = this.x.grid.cellAtCanvas(q.x, q.y), off = this.x.grid.cellOffset(cell.r, cell.c); return [q.id, { anchor: { r: cell.r, c: cell.c, dx: Math.round(q.x - off.x), dy: Math.round(q.y - off.y) }, w: q.w, h: q.h }]; }));
    const list = ((this.x.model.sheet(this.x.sheet())?.props[d.kind] ?? []) as { id: string }[]).map(o => positions.has(o.id) ? { ...o, ...positions.get(o.id) } : o);
    cmd.setProps(this.x, { [d.kind]: list });
  }
  update(kind: Kind, id: string, patch: Partial<ChartSpec & ImageSpec>): void {
    const list = ((this.x.model.sheet(this.x.sheet())?.props[kind] ?? []) as { id: string }[]).map(o => (o.id === id ? { ...o, ...patch } : o));
    cmd.setProps(this.x, { [kind]: list });
  }
  remove(id?: string): void {
    const ids = id ? new Set([id]) : new Set(this.selectedIds); if (!ids.size) return;
    const o = this.list().find(q => ids.has(q.id)); if (!o) return;
    const left = ((this.x.model.sheet(this.x.sheet())?.props[o.kind] ?? []) as { id: string }[]).filter(q => !ids.has(q.id));
    cmd.setProps(this.x, { [o.kind]: left.length ? left : null });
    for (const gone of ids) this.selectedIds.delete(gone);
    this.primary = [...this.selectedIds][0] ?? null;
    this.x.closePanel();
  }
  copySelected(): boolean {
    const images = this.list().filter((o): o is Obj & ImageSpec => o.kind === 'images' && this.selectedIds.has(o.id));
    this.copiedImages = images.map(im => structuredClone(im));
    return images.length > 0;
  }
  pasteCopied(): boolean {
    if (!this.x.canEdit() || !this.copiedImages.length) return false;
    const copies = this.copiedImages.map(im => ({ ...structuredClone(im), id: ruleId(), anchor: { ...im.anchor, dx: im.anchor.dx + 16, dy: im.anchor.dy + 16 } }));
    const images = (this.x.model.sheet(this.x.sheet())?.props.images ?? []) as ImageSpec[];
    cmd.setProps(this.x, { images: [...images, ...copies] });
    this.selectedIds = new Set(copies.map(im => im.id)); this.primary = copies.at(-1)?.id ?? null;
    return true;
  }
  private copy(e: ClipboardEvent): void {
    if (document.activeElement !== this.x.grid.el) return;
    if (!this.copySelected()) return;
    if (!e.clipboardData) return;
    e.preventDefault(); e.stopImmediatePropagation();
    e.clipboardData.setData('text/plain', '');
    e.clipboardData.setData('text/html', '<span data-sheet-images="1">&#8203;</span>');
  }
  private paste(e: ClipboardEvent): void {
    if (document.activeElement !== this.x.grid.el || !this.x.canEdit() || !this.copiedImages.length) return;
    const html = e.clipboardData?.getData('text/html') ?? '', plain = e.clipboardData?.getData('text/plain') ?? '';
    if ((html || plain) && !html.includes('data-sheet-images')) { this.copiedImages = []; return; }
    e.preventDefault(); e.stopImmediatePropagation();
    this.pasteCopied();
  }
  private menu(id: string, kind: Kind, px: number, py: number): void {
    const spec = this.list().find(q => q.id === id); if (!spec) return;
    if (kind === 'slicers') {
      popup([{ label: '슬라이서 수정', disabled: !this.x.canEdit(), action: () => slicerPanel(this.x, id) }, { label: '슬라이서 삭제', disabled: !this.x.canEdit(), action: () => this.remove(id) }], px, py);
      return;
    }
    popup(kind === 'charts' ? [
      { label: '차트 수정', disabled: !this.x.canEdit(), action: () => chartPanel(this.x, id, this) },
      { label: '차트 삭제', disabled: !this.x.canEdit(), action: () => this.remove(id) },
      { separator: true },
      { label: '다운로드 > PNG 이미지(.png)', action: () => downloadChart(spec as ChartSpec, this.svg(spec as ChartSpec), 'png') },
      { label: '다운로드 > SVG(.svg)', action: () => downloadChart(spec as ChartSpec, this.svg(spec as ChartSpec), 'svg') },
    ] : [
      { label: '대체 텍스트', disabled: !this.x.canEdit(), action: async () => { const t = await promptText('대체 텍스트', (spec as ImageSpec).alt ?? ''); if (t !== null) this.update('images', id, { alt: t }); } },
      { label: '이미지 삭제', disabled: !this.x.canEdit(), action: () => this.remove(id) },
      { label: '원래 크기로 재설정', disabled: !this.x.canEdit(), action: () => { const img = new Image(); img.onload = () => this.update('images', id, { w: img.naturalWidth, h: img.naturalHeight }); img.src = (spec as ImageSpec).url; } },
    ], px, py);
  }
}

function downloadChart(spec: ChartSpec, svg: string, kind: 'png' | 'svg'): void {
  const name = `${spec.title || '차트'}.${kind}`;
  if (kind === 'svg') { cmd.download(name, svg, 'image/svg+xml'); return; }
  const img = new Image(), url = URL.createObjectURL(new Blob([svg], { type: 'image/svg+xml' }));
  img.onload = () => {
    const c = document.createElement('canvas'); c.width = spec.w * 2; c.height = spec.h * 2;
    const g = c.getContext('2d')!; g.scale(2, 2); g.drawImage(img, 0, 0); URL.revokeObjectURL(url);
    c.toBlob(b => { if (!b) return; const a = document.createElement('a'); a.href = URL.createObjectURL(b); a.download = name; a.click(); setTimeout(() => URL.revokeObjectURL(a.href), 1000); }, 'image/png');
  };
  img.src = url;
}

// ----- charts -----
export function insertChart(x: PanelCtx, layer: ObjectLayer): void {
  const R = dataRegion(x, x.grid.range);
  const { headers, labels } = guessLayout(x.model, x.sheet(), R);
  const spec: ChartSpec = { id: ruleId(), ...R, type: 'column', headers, labels, anchor: { r: R.r1, c: R.c2 + 2, dx: 0, dy: 0 }, w: 600, h: 371 };
  if (headers) {
    // like Sheets: "A", "A 및 B", "A, B 및 C"
    const names: string[] = [];
    for (let c = R.c1 + (labels ? 1 : 0); c <= R.c2; c++) { const t = x.model.display(x.sheet(), R.r1, c).text; if (t) names.push(t); }
    if (names.length) spec.title = names.length === 1 ? names[0] : `${names.slice(0, -1).join(', ')} 및 ${names[names.length - 1]}`;
  }
  const list = (x.model.sheet(x.sheet())?.props.charts ?? []) as ChartSpec[];
  cmd.setProps(x, { charts: [...list, spec] });
  layer.selected = spec.id;
  const off = x.grid.cellOffset(spec.anchor.r, spec.anchor.c); x.grid.reveal(off.x, off.y);
  chartPanel(x, spec.id, layer);
}
/** Chart editor (side panel): Setup and Customize, applied as you change them. */
export function chartPanel(x: PanelCtx, id: string, layer: ObjectLayer): void {
  const get = (): ChartSpec | undefined => ((x.model.sheet(x.sheet())?.props.charts ?? []) as ChartSpec[]).find(c => c.id === id);
  const spec = get(); if (!spec) return;
  const set = (patch: Partial<ChartSpec>): void => { if (x.canEdit()) layer.update('charts', id, patch); };
  const field = (label: string, ...c: (Node | string)[]): HTMLElement => h('label', { class: 'pf' }, h('span', { class: 'pf-label' }, label), ...c);
  const sel = (opts: [string, string][], v: string | undefined, on: (v: string) => void): HTMLSelectElement => h('select', { class: 'pf-input', onchange: (e: Event) => on((e.target as HTMLSelectElement).value) }, ...opts.map(([k, l]) => h('option', { value: k, selected: k === v }, l)));
  const text = (v: string | undefined, on: (v: string) => void, ph = ''): HTMLInputElement => h('input', { class: 'pf-input', value: v ?? '', placeholder: ph, onchange: (e: Event) => on((e.target as HTMLInputElement).value) });
  const check = (label: string, v: boolean | undefined, on: (v: boolean) => void): HTMLElement => h('label', { class: 'row' }, h('input', { type: 'checkbox', checked: !!v, onchange: (e: Event) => on((e.target as HTMLInputElement).checked) }), ` ${label}`);
  let tab: 'setup' | 'custom' = 'setup';
  const render = (): void => {
    const s = get(); if (!s) { x.closePanel(); return; }
    const data = chartData(x.model, x.sheet(), s), cols = chartColumns(x.model, x.sheet(), s);
    const colName = (c: number): string => (s.headers && x.model.display(x.sheet(), s.r1, c).text) || `${indexToCol(c)}열`;
    const colChoices = (a: number, b: number): [string, string][] => Array.from({ length: b - a + 1 }, (_, i) => [String(a + i), colName(a + i)]);
    const spare = colChoices(s.c1, s.c2).map(([k]) => Number(k)).filter(c => c !== cols.x && !cols.series.includes(c));
    const names = s.type === 'pie' ? data.categories : data.series.map(q => q.name);
    const setup = h('div', { class: 'col' },
      field('차트 유형', sel(CHART_TYPES, s.type, v => { set({ type: v as ChartType }); setTimeout(render); })),
      ['column', 'bar', 'area'].includes(s.type) ? field('스태킹', sel([['', '없음'], ['stacked', '표준'], ['percent', '100%']], s.stacked ?? '', v => set({ stacked: (v || undefined) as ChartSpec['stacked'] }))) : '',
      field('데이터 범위', text(rangeText(x, s), v => { const R = parseRange(x, v); if (!R || R.sheet !== x.sheet()) { toast('범위가 올바르지 않습니다'); return; } set({ r1: R.r1, c1: R.c1, r2: Math.min(R.r2, 999_999), c2: Math.min(R.c2, 18_277), xCol: undefined, series: undefined }); setTimeout(render); })),
      // X축, 집계, 계열 (with 계열 추가), 행/열 전환 - the order of Sheets' Setup tab
      cols.x !== null ? field('X축', sel(colChoices(s.c1, s.c2), String(cols.x), v => { set({ xCol: Number(v), series: undefined }); setTimeout(render); })) : '',
      cols.x !== null ? check('집계', s.aggregate, v => { set({ aggregate: v || undefined }); setTimeout(render); }) : '',
      h('div', { class: 'pf-label' }, '계열'),
      ...cols.series.map(c => h('div', { class: 'row chart-series' }, h('span', { class: 'grow' }, colName(c)), x.canEdit() ? h('button', { class: 'pf-btn', title: '삭제', onclick: () => { set({ series: cols.series.filter(k => k !== c) }); setTimeout(render); } }, '✕') : '')),
      spare.length && x.canEdit() ? field('계열 추가', sel([['', '계열 추가'], ...spare.map(c => [String(c), colName(c)] as [string, string])], '', v => { if (v) { set({ series: [...cols.series, Number(v)].sort((a, b) => a - b) }); setTimeout(render); } })) : '',
      check('행/열 전환', s.switchRC, v => { set({ switchRC: v || undefined }); setTimeout(render); }),
      check(`${s.r1 + 1}행을 헤더로 사용`, s.headers, v => { set({ headers: v }); setTimeout(render); }),
      check(`${indexToCol(s.c1)}열을 라벨로 사용`, s.labels, v => { set({ labels: v, xCol: undefined, series: undefined }); setTimeout(render); }));
    const custom = h('div', { class: 'col' },
      field('차트 제목', text(s.title, v => set({ title: v || undefined }), '제목')),
      s.type === 'pie' ? '' : field('가로축 제목', text(s.xTitle, v => set({ xTitle: v || undefined }))),
      s.type === 'pie' ? '' : field('세로축 제목', text(s.yTitle, v => set({ yTitle: v || undefined }))),
      field('범례', sel([['', '자동'], ['right', '오른쪽'], ['bottom', '아래'], ['top', '위'], ['none', '없음']], s.legend ?? '', v => set({ legend: (v || undefined) as ChartSpec['legend'] }))),
      ['line', 'combo', 'area'].includes(s.type) ? check('부드러운 선', s.smooth, v => set({ smooth: v || undefined })) : '',
      ['line', 'combo', 'area'].includes(s.type) ? check('점 표시', s.points, v => set({ points: v || undefined })) : '',
      s.type === 'pie' ? check('도넛 모양', s.donut, v => set({ donut: v || undefined })) : '',
      h('div', { class: 'pf-label' }, s.type === 'pie' ? '조각 색상' : '시리즈 색상'),
      ...names.slice(0, 20).map((n, i) => h('label', { class: 'row' }, h('input', { type: 'color', value: s.colors?.[i] || PALETTE[i % PALETTE.length], onchange: (e: Event) => { const cs = [...(s.colors ?? [])]; while (cs.length <= i) cs.push(''); cs[i] = (e.target as HTMLInputElement).value; set({ colors: cs }); } }), ` ${n}`)));
    x.openPanel('차트 편집기', h('div', { class: 'panel' },
      h('div', { class: 'pf-tabs' }, ...(['setup', 'custom'] as const).map(k => h('button', { class: `pf-tab${tab === k ? ' on' : ''}`, onclick: () => { tab = k; render(); } }, k === 'setup' ? '설정' : '맞춤설정'))),
      tab === 'setup' ? setup : custom,
      x.canEdit() ? h('div', { class: 'pf-buttons' }, h('button', { class: 'pf-btn', onclick: () => layer.remove(id) }, '차트 삭제')) : ''));
  };
  render();
}

// ----- images -----
async function pickImage(): Promise<File | null> {
  return new Promise(resolve => {
    const input = h('input', { type: 'file', accept: 'image/png,image/jpeg,image/gif,image/webp' });
    input.onchange = () => resolve(input.files?.[0] ?? null);
    input.click();
  });
}
async function upload(x: PanelCtx, file: File): Promise<string | null> {
  if (file.size > 10 * 1024 * 1024) { toast('10MB 이하의 이미지만 업로드할 수 있습니다'); return null; }
  const r = await fetch(`/api/docs/${x.docId}/files`, { method: 'POST', headers: { 'x-requested-with': 'sheet', 'content-type': 'application/octet-stream' }, body: file });
  const j = await r.json().catch(() => ({}));
  if (!r.ok) { toast(r.status === 415 ? 'PNG, JPEG, GIF, WebP 이미지만 넣을 수 있습니다' : `업로드하지 못했습니다: ${j.error ?? r.status}`); return null; }
  return j.url as string;
}
const imageSize = (url: string): Promise<{ w: number; h: number }> => new Promise(resolve => { const i = new Image(); i.onload = () => resolve({ w: i.naturalWidth, h: i.naturalHeight }); i.onerror = () => resolve({ w: 300, h: 200 }); i.src = url; });

/** Insert > Image > over cells (upload or URL). */
export async function insertImageOver(x: PanelCtx, layer: ObjectLayer, byUrl = false): Promise<void> {
  let url: string | null;
  if (byUrl) { url = (await promptText('URL로 이미지 삽입', 'https://', '이미지 주소'))?.trim() ?? null; if (url && !/^https?:\/\//i.test(url)) { toast('http(s) 주소만 사용할 수 있습니다'); return; } }
  else { const f = await pickImage(); url = f ? await upload(x, f) : null; }
  if (!url) return;
  const size = await imageSize(url), scale = Math.min(1, 400 / size.w, 300 / size.h);
  const R = x.grid.range, img: ImageSpec = { id: ruleId(), url, anchor: { r: R.r1, c: R.c1, dx: 0, dy: 0 }, w: Math.round(size.w * scale), h: Math.round(size.h * scale) };
  cmd.setProps(x, { images: [...((x.model.sheet(x.sheet())?.props.images ?? []) as ImageSpec[]), img] });
  layer.selected = img.id;
}
/** Insert > Drawing (or a double-click on a drawing): edit, then place the picture over the cells. */
export async function insertDrawing(x: PanelCtx, layer: ObjectLayer, existing?: ImageSpec): Promise<void> {
  const res = await drawingEditor(existing?.drawing ?? []);
  if (!res) return;
  const r = await fetch(`/api/docs/${x.docId}/files`, { method: 'POST', headers: { 'x-requested-with': 'sheet', 'content-type': 'application/octet-stream' }, body: res.blob });
  if (!r.ok) { toast('그림을 저장하지 못했습니다'); return; }
  const url = (await r.json()).url as string;
  if (existing) { layer.update('images', existing.id, { url, w: res.w, h: res.h, drawing: res.shapes } as Partial<ChartSpec & ImageSpec>); return; }
  const R = x.grid.range, img: ImageSpec = { id: ruleId(), url, anchor: { r: R.r1, c: R.c1, dx: 0, dy: 0 }, w: res.w, h: res.h, drawing: res.shapes };
  cmd.setProps(x, { images: [...((x.model.sheet(x.sheet())?.props.images ?? []) as ImageSpec[]), img] });
  layer.selected = img.id;
}
/** Insert > Image > in cell: an IMAGE() formula in the selected cell. */
export async function insertImageInCell(x: PanelCtx, byUrl = false): Promise<void> {
  let url: string | null;
  if (byUrl) { url = (await promptText('셀에 이미지 삽입', 'https://', '이미지 주소'))?.trim() ?? null; if (url && !/^https?:\/\//i.test(url)) { toast('http(s) 주소만 사용할 수 있습니다'); return; } }
  else { const f = await pickImage(); url = f ? await upload(x, f) : null; }
  if (!url) return;
  const { r, c } = x.grid.sel;
  x.submit({ t: 'set', sheet: x.sheet(), r, c, raw: `=IMAGE("${url.replace(/"/g, '""')}")` });
}

// ----- people chips (Insert > Smart chips > People, or "@" in a cell) -----
export async function insertPerson(x: PanelCtx, query = ''): Promise<void> {
  const input = h('input', { class: 'dlg-input', value: query, placeholder: '이름 또는 이메일 검색' });
  const list = h('div', { class: 'search-list' });
  let users: { username: string; email: string | null; displayName: string | null }[] = [], timer = 0;
  const search = async (): Promise<void> => {
    if (input.value.trim().length < 1) { list.replaceChildren(); return; }
    try { users = (await api<{ users: typeof users }>('GET', `/api/users/search?q=${encodeURIComponent(input.value.trim())}`)).users; }
    catch (e) { if (e instanceof ApiError) users = []; }
    list.replaceChildren(...users.map(u => h('div', { class: 'search-item', onmousedown: () => choose(u) }, h('b', {}, u.displayName ?? u.username), h('span', { class: 'muted' }, ` ${u.email ?? u.username}`))));
  };
  const choose = (u: (typeof users)[number]): void => {
    input.closest('.dlg-back')?.remove();
    const { r, c } = x.grid.sel, s = x.sheet();
    const ops: Op[] = [{ t: 'set', sheet: s, r, c, raw: u.displayName ?? u.username }, { t: 'fmt', sheet: s, r1: r, c1: c, r2: r, c2: c, fmt: { person: u.email ?? u.username } }];
    x.submit({ t: 'batch', ops });
    x.grid.el.focus();
  };
  input.addEventListener('input', () => { clearTimeout(timer); timer = window.setTimeout(search, 200); });
  input.addEventListener('keydown', e => { if (e.key === 'Enter' && users[0]) { e.preventDefault(); choose(users[0]); } });
  if (query) search();
  dialog('사용자 칩 삽입', h('div', { class: 'col' }, input, list), [{ label: '닫기', value: 'x' }]);
  setTimeout(() => input.focus());
}
export function openPerson(email: string, px: number, py: number): void {
  popup([
    { label: email, disabled: true },
    { separator: true },
    { label: '이메일 보내기', action: () => { if (/^[^\s@]+@[^\s@]+$/.test(email)) location.href = `mailto:${email}`; else toast('이메일 주소가 없는 사용자입니다'); } },
    { label: '이메일 주소 복사', action: () => { navigator.clipboard.writeText(email).then(() => toast('복사했습니다')); } },
  ], px, py);
}
